import { assertCanHandle, isNil } from '@lucidlink/tscore';
import { AuxDataKey, createAuxData, CRYPTO_TAG_LENGTH, } from '#block-store/types';
import { GetRequestStatsContext } from '#block-store/utils';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { CryptoError } from '#common/types';
import { canHandleAllErrors } from '#common/util';
import { createAesCrypto } from '#crypto/web-crypto';
import { CryptoStats } from './types';
export const createCryptoStore = (blockStore, mod) => {
    return new CryptoStore(blockStore, mod);
};
class CryptoStore {
    blockStore;
    mod;
    log = Logging.getLogger('CryptoStore');
    cryptoStats = new CryptoStats(this.log);
    crypto = createAesCrypto();
    constructor(blockStore, mod) {
        this.blockStore = blockStore;
        this.mod = mod;
    }
    async get(request) {
        const context = new GetRequestStatsContext(request, this.cryptoStats.get);
        try {
            const blockPromises = await this.blockStore.get(request);
            return blockPromises.map((promise, index) => this.decryptBlockAsync(promise, index + request.startBlockId, request.cryptoKey, context));
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            context.onRequestFailed();
            throw err;
        }
    }
    async put(request) {
        const key = request.cryptoKey;
        await withStats(this.cryptoStats.put, async () => {
            const blocks = request.blocks;
            const encryptPromises = this.scheduleEncryptOperations(blocks, key);
            const settledResult = await Promise.allSettled(encryptPromises);
            this.processEncryptionResult(blocks, settledResult);
        }, { id: request.inodeId, size: request.blocks.length });
        await this.blockStore.put(request);
    }
    stats() {
        return this.cryptoStats;
    }
    async stop() {
        await this.blockStore.stop();
    }
    async decryptBlockAsync(blockPromise, blockId, key, context) {
        const block = await this.awaitBlockPromise(blockPromise, context);
        try {
            const wasDecrypted = await this.decryptBlock(block, key);
            if (wasDecrypted) {
                ++this.cryptoStats.totalBlocksDecrypted;
                this.cryptoStats.totalBytesDecrypted += block.data.dataBuffer.byteLength;
            }
            context.onGetSucceeded();
            return block;
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            ++this.cryptoStats.totalDecryptFailures;
            context.onGetFailed();
            const details = { fileId: context.request.inodeId, blockId: blockId, reason: err };
            this.log.error(details, 'Failed to decrypt block.');
            throw new CryptoError('Failed to decrypt block', JSON.stringify(details));
        }
    }
    async awaitBlockPromise(blockPromise, context) {
        try {
            return await blockPromise;
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            context.onGetFailed();
            throw err;
        }
    }
    async decryptBlock(block, key) {
        const auxData = this.deserializeAuxData(block.data.auxBuffer, block.blockId);
        if (isNil(auxData)) {
            return false; // no aux // TODO: Should be refactored with LUCID-21742
        }
        const { iv, tag } = auxData;
        const encryptedData = new Uint8Array(block.data.dataBuffer.byteLength + tag.byteLength);
        encryptedData.set(block.data.dataBuffer, 0);
        encryptedData.set(tag, block.data.dataBuffer.byteLength);
        const decryptedData = await this.crypto.decrypt({ value: encryptedData, iv }, key);
        block.data.dataBuffer = new Uint8Array(decryptedData);
        return true;
    }
    scheduleEncryptOperations(blocks, key) {
        const encryptPromises = [];
        for (const block of blocks) {
            encryptPromises.push(this.crypto.encrypt(block.data.dataBuffer, key));
        }
        return encryptPromises;
    }
    deserializeAuxData(auxBuffer, blockId) {
        const auxData = this.mod.deserializeAuxData(auxBuffer);
        const iv = auxData.get(AuxDataKey.CRYPTO_IV);
        const tag = auxData.get(AuxDataKey.CRYPTO_CHECKSUM);
        if (isNil(iv) && isNil(tag)) {
            return undefined; // No crypto data present
        }
        if (isNil(iv)) {
            throw new CryptoError(`No iv found in the auxiliary data for block ${blockId}`);
        }
        if (isNil(tag)) {
            throw new CryptoError(`No tag found in the auxiliary data for block ${blockId}`);
        }
        return { iv, tag };
    }
    addToAuxData(block, encryptedLength, iv, tag) {
        const auxData = isNil(block.data.auxBuffer)
            ? createAuxData()
            : this.mod.deserializeAuxData(block.data.auxBuffer);
        auxData.set(AuxDataKey.CRYPTO_IV, iv);
        auxData.set(AuxDataKey.CRYPTO_CHECKSUM, tag);
        block.data.auxBuffer = this.mod.serializeAuxData(auxData, encryptedLength - CRYPTO_TAG_LENGTH);
    }
    processEncryptionResult(blocks, settledResult) {
        let numFailedEncryptions = 0;
        settledResult.forEach((promise, index) => {
            const block = blocks[index];
            if (promise.status === 'fulfilled') {
                const encrypted = promise.value.value;
                ++this.cryptoStats.totalBlocksEncrypted;
                this.cryptoStats.totalBytesEncrypted += block.data.dataBuffer.byteLength;
                const tag = encrypted.subarray(-CRYPTO_TAG_LENGTH);
                this.addToAuxData(block, encrypted.length, promise.value.iv, tag);
                block.data.dataBuffer = encrypted.subarray(0, encrypted.length - CRYPTO_TAG_LENGTH);
            }
            else {
                ++numFailedEncryptions;
                ++this.cryptoStats.totalEncryptFailures;
                this.log.error({ blockId: block.blockId, reason: promise.reason }, 'Failed to encrypt block.');
            }
        });
        if (numFailedEncryptions > 0) {
            throw new CryptoError(`Failed to encrypt ${numFailedEncryptions} blocks`);
        }
    }
}
